import { useForm } from 'react-hook-form';
import { Network } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PCMP_DIVISION_LABELS, PCMP_NOT_MAPPED_LABEL } from '../../constants/pcmpQueue.constants';
import { useSavePcmpDepartmentDivision } from '../../hooks/useSavePcmpDepartmentDivision';
import type { PcmpLookupDepartment } from '../../types/pcmpLookups.types';
import { PCMP_DIVISIONS, type PcmpDivision } from '../../types/pcmpQueue.types';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const NOT_MAPPED = '__none__';

const DIVISION_ITEMS = [
  { value: NOT_MAPPED, label: PCMP_NOT_MAPPED_LABEL },
  ...PCMP_DIVISIONS.map((code) => ({ value: code as string, label: PCMP_DIVISION_LABELS[code] })),
];

interface FormValues {
  division: string;
}

interface PcmpDepartmentDivisionDialogProps {
  department: PcmpLookupDepartment;
  onClose: () => void;
}

/** Sets which PCMP division a department's MOs are reported under. */
export function PcmpDepartmentDivisionDialog({
  department,
  onClose,
}: PcmpDepartmentDivisionDialogProps) {
  const form = useForm<FormValues>({
    defaultValues: {
      division: PCMP_DIVISIONS.find((code) => code === department.divisionCode) ?? NOT_MAPPED,
    },
  });
  const { mutate, isPending, error } = useSavePcmpDepartmentDivision(department.id, onClose);

  const onSubmit = form.handleSubmit(({ division }) =>
    mutate(division === NOT_MAPPED ? null : (division as PcmpDivision)),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Set PCMP division"
        description={department.name}
        icon={Network}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        submitDisabled={!form.formState.isDirty}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Save"
        pendingLabel="Saving..."
      >
        <FormField
          control={form.control}
          name="division"
          render={({ field }) => (
            <FormItem>
              <FormLabel>PCMP division</FormLabel>
              <Select<string>
                items={DIVISION_ITEMS}
                value={field.value}
                onValueChange={(next) => next !== null && field.onChange(next)}
              >
                <FormControl>
                  <SelectTrigger aria-label="PCMP division" className="w-full">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {DIVISION_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      </PcmpEditDialog>
    </Form>
  );
}
